/**
 * VerdictVerifView — Rendu observations branche Vérif.
 * Affiche les observations à 3 temps : constat → droit_general → question (action).
 * Plus les angles non couverts en bas.
 */
import type { VerdictData, VerifObservation, VerifPro } from "@/hooks/use-verdict";
import { useState } from "react";
import { verdictProseStyle } from "@/lib/verdict-typography";

const PRO_LABEL: Record<VerifPro, string> = {
  avocat: "Avocat",
  expert_comptable: "Expert-comptable",
  notaire: "Notaire",
  conseil_pi: "Conseil en propriété industrielle",
  banquier: "Banquier",
  autre: "Autre conseil",
};

const CONVERGENCE_LABEL: Record<string, string> = {
  faible: "Relevé par 1 conseiller",
  moyen: "Relevé par 2-3 conseillers",
  fort: "Relevé par 4+ conseillers",
};

const CONVERGENCE_COLOR: Record<string, string> = {
  faible: "rgba(255,23,68,0.45)",
  moyen: "#FF1744",
  fort: "#FF1744",
};

const proLabel = (p: VerifObservation["pro_a_consulter"]): string =>
  PRO_LABEL[p] ?? "Conseil";
const convLabel = (c: string): string =>
  CONVERGENCE_LABEL[c] ?? "Convergence relevée";
const convColor = (c: string): string =>
  CONVERGENCE_COLOR[c] ?? "#FF1744";


const GOLD = "#F5EFE3";
const GOLD_SOFT = "rgba(255,23,68,0.28)";
const INK = "var(--qv3-ink, #1F1F22)";
const MUTED = "var(--qv3-grey-500, #6B6D71)";
const SURFACE = "rgba(255,23,68,0.05)";

export function VerdictVerifView({ data }: { data: VerdictData }) {
  const obs = data.verifObservations;
  if (!obs) {
    return (
      <article aria-label="Observations en attente">
        <Header data={data} />
        <p
          role="status"
          style={{
            padding: "60px 24px",
            textAlign: "center",
            ...verdictProseStyle,
            color: MUTED,
          }}
        >
          Le Conseil termine la synthèse des observations…
        </p>
      </article>
    );
  }

  return (
    <article aria-label={`Observations — saisine ${data.saisineNumber}`}>
      <Header data={data} />

      {obs.rappel_faits ? (
        <section
          aria-label="Votre dossier, en résumé"
          style={{
            marginBottom: 32,
            padding: "24px 28px",
            border: `1px solid ${GOLD_SOFT}`,
            borderLeft: `3px solid ${GOLD}`,
            background: SURFACE,
          }}
        >
          <div
            style={{
              fontFamily: '"DM Mono", "JetBrains Mono", monospace',
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 12,
            }}
          >
            · En clair ·
          </div>
          <p
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 18,
              lineHeight: 1.55,
              color: INK,
              margin: 0,
              whiteSpace: "pre-wrap",
            }}
          >
            {obs.rappel_faits}
          </p>
        </section>
      ) : null}



      {obs.observations.length === 0 ? (
        <div
          style={{
            padding: "40px 24px",
            textAlign: "center",
            border: `1px solid ${GOLD_SOFT}`,
            background: SURFACE,
          }}
        >
          <p
            style={{
              ...verdictProseStyle,
              color: INK,
              margin: 0,
            }}
          >
            Aucun point particulier relevé sur ce dossier.
          </p>
          <p
            style={{
              fontFamily: '"DM Sans", sans-serif',
              fontSize: 14,
              color: MUTED,
              margin: "12px 0 0",
            }}
          >
            Le Conseil a examiné votre dossier sans identifier de zone à clarifier.
          </p>
        </div>
      ) : (
        <section
          aria-label="Observations"
          style={{ display: "flex", flexDirection: "column", gap: 20, marginBottom: 48 }}
        >
          {obs.observations.map((o, i) => (
            <ObservationCard key={i} observation={o} index={i + 1} />
          ))}
        </section>
      )}

      {obs.angles_non_couverts.length > 0 && (
        <section
          aria-label="Aspects à voir ailleurs"
          style={{
            marginTop: 32,
            padding: "20px 22px",
            border: `1px dashed ${GOLD_SOFT}`,
            background: SURFACE,
          }}
        >
          <div
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 12,
            }}
          >
            Aspects à voir ailleurs
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: INK, fontSize: 15, lineHeight: 1.6 }}>
            {obs.angles_non_couverts.map((a, i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                {a}
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  );
}

function Header({ data }: { data: VerdictData }) {
  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: `1px solid ${GOLD_SOFT}`,
          fontFamily: '"DM Mono", monospace',
          fontSize: 11,
          letterSpacing: 2,
          textTransform: "uppercase",
          color: GOLD,
        }}
      >
        <span>Audit · {data.saisineNumber || "—"}</span>
        <span>
          {data.saisineDate} · {data.saisineTime}
        </span>
      </div>

      <p
        style={{
          fontFamily: '"DM Sans", sans-serif',
          fontStyle: "normal",
          fontWeight: 500,
          fontSize: 17,
          lineHeight: 1.55,
          color: "var(--charcoal, #1F1F22)",
          margin: "0 0 16px",
        }}
      >
        « {data.question} »
      </p>
      <p
        style={{
          fontFamily: '"DM Sans", sans-serif',
          fontSize: 13,
          lineHeight: 1.55,
          color: MUTED,
          margin: "0 0 40px",
        }}
      >
        Le Conseil a examiné votre dossier. Voici les observations à confronter
        à votre avocat, expert-comptable, banquier ou autre conseil.
      </p>
    </>
  );
}

function ObservationCard({
  observation,
  index,
}: {
  observation: VerifObservation;
  index: number;
}) {
  const [showAnchor, setShowAnchor] = useState(false);
  const cColor = convColor(observation.convergence);
  const cLabel = convLabel(observation.convergence);
  return (
    <article
      style={{
        border: `1px solid ${GOLD_SOFT}`,
        background: "#FFFCF4",
        padding: "22px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 10,
            letterSpacing: 2,
            textTransform: "uppercase",
            color: GOLD,
          }}
        >
          Observation #{index}
          {observation.angle ? ` · Angle ${observation.angle}` : ""}
        </span>
        <span
          aria-label={cLabel}
          title={cLabel}
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 10,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            padding: "3px 10px",
            border: `1px solid ${cColor}`,
            color: cColor,
            borderRadius: 999,
          }}
        >
          Convergence · {observation.convergence || "—"}
        </span>
      </header>


      {/* 1. Constat — le fait, mis en avant */}
      {observation.constat && (
        <p
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 19,
            lineHeight: 1.45,
            color: INK,
            margin: 0,
          }}
        >
          {observation.constat}
        </p>
      )}

      {/* Ancrage — repliable */}
      {observation.ancrage && (
        <div>
          <button
            type="button"
            onClick={() => setShowAnchor((v) => !v)}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontFamily: '"DM Mono", monospace',
              fontSize: 10,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: MUTED,
            }}
          >
            {showAnchor ? "− Masquer le passage concerné" : "+ Voir le passage concerné"}
          </button>
          {showAnchor && (
            <blockquote
              style={{
                margin: "10px 0 0",
                padding: "10px 14px",
                borderLeft: `2px solid ${GOLD_SOFT}`,
                fontFamily: '"DM Sans", sans-serif',
                fontSize: 13,
                lineHeight: 1.6,
                color: MUTED,
                background: SURFACE,
                whiteSpace: "pre-wrap",
              }}
            >
              {observation.ancrage}
            </blockquote>
          )}
        </div>
      )}

      {/* 2. Droit général — la règle, ton secondaire */}
      {observation.droit_general && (
        <p
          style={{
            fontFamily: '"DM Sans", sans-serif',
            fontSize: 13,
            lineHeight: 1.6,
            color: MUTED,
            margin: 0,
            paddingTop: 10,
            borderTop: `1px solid ${GOLD_SOFT}`,
          }}
        >
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 9,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: GOLD,
              marginRight: 8,
            }}
          >
            Règle générale
          </span>
          {observation.droit_general}
        </p>
      )}

      {/* 3. Question — l'action, mise en avant + chip pro */}
      {observation.question && (
        <div
          style={{
            marginTop: 4,
            padding: "14px 16px",
            background: "rgba(255,23,68,0.08)",
            border: `1px solid ${GOLD}`,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 10,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: GOLD,
            }}
          >
            Question à poser
          </span>
          <p
            style={{
              ...verdictProseStyle,
              color: INK,
              margin: 0,
            }}
          >
            « {observation.question} »
          </p>
          <span
            style={{
              alignSelf: "flex-start",
              fontFamily: '"DM Mono", monospace',
              fontSize: 10,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              padding: "4px 10px",
              background: INK,
              color: "#FFFCF4",
              borderRadius: 2,
            }}
          >
            À votre {proLabel(observation.pro_a_consulter)}
          </span>
        </div>
      )}
    </article>
  );
}

export default VerdictVerifView;
